import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui";
import { useBuilderIdentity } from "../workflowBuilder/useBuilderIdentity";
import { listHomeWorkflowCatalog } from "./api";
import type { HomeCandidate } from "./models";
import styles from "./HomeWorkflows.module.css";

interface Props {
    disabled: boolean;
    onSelect: (workflow: HomeCandidate) => void;
}

export function ManualWorkflowChoice({ disabled, onSelect }: Props) {
    const { t } = useTranslation();
    const { resolveToken } = useBuilderIdentity();
    const panelId = useId();
    const [open, setOpen] = useState(false);
    const [kind, setKind] = useState<HomeCandidate["kind"]>("personal");
    const [query, setQuery] = useState("");
    const [candidates, setCandidates] = useState<HomeCandidate[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!open || disabled) return;
        const controller = new AbortController();
        setLoading(true);
        setError("");
        setCandidates([]);
        const timeout = setTimeout(() => {
            controller.abort();
            setLoading(false);
            setError(t("manualWorkflow.loadFailed"));
        }, 30_000);
        void (async () => {
            try {
                const token = await resolveToken();
                if (controller.signal.aborted) return;
                const available = await listHomeWorkflowCatalog(token, controller.signal);
                if (!controller.signal.aborted) setCandidates(available);
            } catch {
                if (!controller.signal.aborted) setError(t("manualWorkflow.loadFailed"));
            } finally {
                clearTimeout(timeout);
                if (!controller.signal.aborted) setLoading(false);
            }
        })();
        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [open, disabled, attempt, resolveToken, t]);

    const needle = query.trim().toLocaleLowerCase();
    const visible = candidates.filter(
        candidate => candidate.kind === kind && `${candidate.title} ${candidate.description}`.toLocaleLowerCase().includes(needle)
    );

    return (
        <div className={styles.manualChoice}>
            <Button variant="ghost" size="sm" disabled={disabled} aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(value => !value)}>
                {t("manualWorkflow.use")}
            </Button>
            {open && !disabled && (
                <section id={panelId} className={styles.manualPanel} aria-label={t("manualWorkflow.choose")}>
                    <p>{t("manualWorkflow.help")}</p>
                    <div className={styles.manualKinds} role="group" aria-label={t("manualWorkflow.kind")}>
                        {(["personal", "admin"] as const).map(option => (
                            <Button key={option} size="sm" aria-pressed={kind === option} onClick={() => setKind(option)}>
                                {option === "personal" ? "Personal Workflow" : "Admin Workflow"}
                            </Button>
                        ))}
                    </div>
                    <input
                        className={styles.manualSearch}
                        type="search"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        placeholder={t("manualWorkflow.search")}
                        aria-label={t("manualWorkflow.search")}
                    />
                    {loading ? (
                        <p role="status">{t("manualWorkflow.loading")}</p>
                    ) : error ? (
                        <div role="alert">
                            <p>{error}</p>
                            <Button size="sm" onClick={() => setAttempt(value => value + 1)}>
                                {t("manualWorkflow.retry")}
                            </Button>
                        </div>
                    ) : (
                        <div className={styles.manualOptions}>
                            {visible.length === 0 && <p role="status">{t("manualWorkflow.empty")}</p>}
                            {visible.map(candidate => (
                                <button
                                    className={styles.manualOption}
                                    type="button"
                                    key={`${candidate.kind}:${candidate.id}`}
                                    onClick={() => {
                                        setOpen(false);
                                        onSelect(candidate);
                                    }}
                                >
                                    <strong>{candidate.title}</strong>
                                    {candidate.description && <span>{candidate.description}</span>}
                                </button>
                            ))}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}
